import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Star, Instagram } from "lucide-react";
import { UGC_IMAGES, SITE } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SocialProof() {
  const [reviews, setReviews] = useState([]);
  useEffect(() => {
    axios.get(`${API}/reviews`).then((r) => setReviews(r.data)).catch(() => setReviews([]));
  }, []);

  return (
    <section data-testid="social-proof-section" className="relative py-24 md:py-32 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.22em] text-[#f5f0ea] font-semibold">
              People are talking
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mt-3 tracking-tighter text-white">
              Loved by <span className="text-[#A7D38B]">4.9</span>k+ travellers.
            </h2>
          </div>
          <a
            href={SITE.instagram}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-[#A7D38B] transition"
            data-testid="ig-link"
          >
            <Instagram size={16} /> @gonomikai
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {reviews.map((rev, i) => (
            <motion.div
              key={rev.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="rounded-3xl bg-neutral-900 border border-white/5 p-5 hover:border-[#A7D38B]/30 transition-colors"
              data-testid={`review-${rev.id}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-[#62794d] text-[#f5f0ea] font-bold flex items-center justify-center">
                  {rev.initials}
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-white">{rev.name}</div>
                  <div className="text-[11px] text-neutral-500">{rev.location} · {rev.stay_type}</div>
                </div>
              </div>
              <div className="flex gap-0.5 mb-2 text-[#f5d27a]">
                {Array.from({ length: rev.rating }).map((_, k) => (
                  <Star key={k} size={14} fill="currentColor" />
                ))}
              </div>
              <p className="text-sm text-neutral-300 leading-relaxed">"{rev.text}"</p>
            </motion.div>
          ))}
        </div>

        <div className="mb-6 flex items-center justify-between">
          <h3 className="font-display text-2xl sm:text-3xl font-semibold text-white">@gonomikai · from our guests</h3>
          <span className="text-xs text-neutral-500 hidden sm:block">Real reels · real people</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ugc-grid">
          {UGC_IMAGES.map((src, i) => (
            <motion.a
              key={src}
              href={SITE.instagram}
              target="_blank"
              rel="noreferrer"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.04 }}
              whileHover={{ scale: 1.02 }}
              className="relative aspect-square rounded-2xl overflow-hidden border border-white/5 group"
              data-testid={`ugc-${i}`}
            >
              <img src={src} alt="UGC" loading="lazy" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                <Instagram className="text-white" size={24} />
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
